// How a lead write reaches the cached lead lists.
//
// Tabell's list is the expensive query: every lead in scope, 8.7 s p50 and
// about 9 MB before gzip in prod at 6000 leads. Which leads it holds depends
// on country, search and stage, none of which an ownership write changes, so
// a write patches its row from the server's response instead of refetching
// it. Focus's queue is cheap (0.5 s p50) and is decided by ownership and the
// promise window, which the client cannot fully replay, so a write that can
// move a lead into or out of a queue refetches the Focus lists alone.
import type { Query, QueryClient } from '@tanstack/react-query'
import { CLOSED_STAGES, EXCLUDED_SF_STATUSES } from '@/shared/domain'
import type { CardOnlyField, Lead, LeadRow } from '@/shared/types'
import { apiFetch, type LeadsResponse } from './core'

// useLeads keys are ['leads', segment, search, stagesKey, showAll, queue], where
// queue is asUser or TEST_QUEUE.
// The map's ['leads', 'geo'] and ['leads', 'external'] share the prefix but
// hold other shapes, so a row write must not reach them.
export const LEAD_LISTS = {
  queryKey: ['leads'],
  predicate: (q: Query) => q.queryKey[1] === 'all' || q.queryKey[1] === 'focus',
}

// Every CardOnlyField once; the Record makes tsc reject a missing or extra key.
const CARD_ONLY: Record<CardOnlyField, true> = {
  bdr: true, employees: true, website: true, revenue: true,
  assignedAt: true, meetingOutcome: true, comment: true, nextContactNote: true,
  sfLastActivityAt: true, sfOpenOpportunityStage: true, updatedAt: true,
  primaryContactOverride: true, handover: true, timeline: true, notes: true,
}

/**
 * The row GET /api/leads would list for this lead: the lead without its
 * card-only fields. The open card reads those from ['lead', id] (see
 * useLeadDetail).
 */
export function toListRow(lead: LeadRow): LeadRow {
  const row: Partial<Lead> = { ...lead }
  for (const key of Object.keys(CARD_ONLY) as CardOnlyField[]) delete row[key]
  return row as LeadRow
}

/**
 * The card for a row whose full lead has not arrived yet: the card-only fields
 * the row lacks empty, and the row's contact columns as the primary contact.
 */
export function rowAsLead(row: LeadRow): Lead {
  return {
    bdr: '', employees: '', website: '', revenue: '',
    assignedAt: null, meetingOutcome: null, comment: '',
    sfLastActivityAt: null, sfOpenOpportunityStage: null, updatedAt: null,
    primaryContactOverride: null,
    handover: {
      opportunity: '', pitchAngle: '', timing: '', signals: [], decisionMakers: [], reasoning: '',
      primary: {
        name: row.contact, role: row.contactRole, email: row.email,
        emailStatus: row.emailStatus, phone: row.phone,
      },
    },
    timeline: [],
    notes: [],
    ...row,
  }
}

/**
 * Write a lead the server returned into every cached list that holds it, and
 * the full card into ['lead', id]. Lists without the lead are not written at
 * all: setQueryData marks a query fresh, which would cancel a pending Focus
 * refetch.
 */
export function spliceLeadIntoCache(qc: QueryClient, lead: Lead): void {
  const row = toListRow(lead)
  for (const [key, old] of qc.getQueriesData<LeadsResponse>(LEAD_LISTS)) {
    if (!old?.leads.some(l => l.id === lead.id)) continue
    qc.setQueryData<LeadsResponse>(key, {
      ...old, leads: old.leads.map(l => (l.id === lead.id ? row : l)),
    })
  }
  qc.setQueryData<Lead>(['lead', lead.id], lead)
}

const CLOSED = new Set<string>(CLOSED_STAGES)
const EXCLUDED_SF = new Set<string>(EXCLUDED_SF_STATUSES)

/**
 * A lead new to the CRM joins every unsearched table list whose filters show
 * it, the same stage and Salesforce checks the server's `_base_filters` runs.
 * Tabell sorts its rows itself, so the front of the array is as good as any.
 * A searched list cannot tell whether the name matches, so it refetches.
 */
export function addLeadToTables(qc: QueryClient, lead: Lead): void {
  const row = toListRow(lead)
  const shown = (stagesKey: unknown) => {
    const included = new Set(String(stagesKey ?? '').split(','))
    if (CLOSED.has(lead.stage) && !included.has(lead.stage)) return false
    if (included.has('Existing Customer')) return true
    return !(lead.sfStatus && EXCLUDED_SF.has(lead.sfStatus)) && !lead.sfOpenOpportunityStage
  }
  const tables = { queryKey: ['leads', 'all'], predicate: (q: Query) => q.queryKey[2] === '' }
  for (const [key, old] of qc.getQueriesData<LeadsResponse>(tables)) {
    if (!old || !shown(key[3]) || old.leads.some(l => l.id === lead.id)) continue
    qc.setQueryData<LeadsResponse>(key, { ...old, leads: [row, ...old.leads], total: old.total + 1 })
  }
  void qc.invalidateQueries({ queryKey: ['leads', 'all'], predicate: q => q.queryKey[2] !== '' })
}

/** After a write that can move a lead into or out of someone's queue. */
export function refetchFocusLists(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: ['leads', 'focus'] })
}

/**
 * For a write whose response is not the lead (a correction, a deal change):
 * refetch that one lead and patch it into the lists. If the fetch fails the
 * rows stay as they were until their list's next fetch. A caller that also
 * refetches Focus does so after this settles, so the splice cannot mark a
 * just-invalidated Focus list fresh.
 */
export async function refreshLeadInLists(qc: QueryClient, id: string): Promise<void> {
  try {
    const lead = await qc.fetchQuery({
      queryKey: ['lead', id],
      queryFn: () => apiFetch<Lead>(`/api/leads/${id}`),
      staleTime: 0,
    })
    spliceLeadIntoCache(qc, lead)
  } catch {
    // The card shows the query's error state; the lists keep their rows.
  }
}

/**
 * A lead that can no longer be worked (marked Kontakta ej) leaves every cached
 * Focus queue at once, without the refetch: nothing about it can bring it back
 * into a queue until the mark is lifted. Tabell keeps its row, which
 * spliceLeadIntoCache updates in place.
 */
export function dropLeadFromFocus(qc: QueryClient, id: string): void {
  for (const [key, old] of qc.getQueriesData<LeadsResponse>({ queryKey: ['leads', 'focus'] })) {
    if (!old?.leads.some(l => l.id === id)) continue
    qc.setQueryData<LeadsResponse>(key, {
      ...old, leads: old.leads.filter(l => l.id !== id), total: Math.max(0, old.total - 1),
    })
  }
}

/**
 * After two duplicate companies merge into one: the dropped company leaves
 * every cached list and its card cache, and the kept lead's row takes the
 * merged owner, stage and fields wherever it is listed.
 */
export function mergeLeadInCache(qc: QueryClient, kept: Lead, dropId: string): void {
  for (const [key, old] of qc.getQueriesData<LeadsResponse>(LEAD_LISTS)) {
    if (!old?.leads.some(l => l.id === dropId)) continue
    qc.setQueryData<LeadsResponse>(key, {
      ...old, leads: old.leads.filter(l => l.id !== dropId), total: Math.max(0, old.total - 1),
    })
  }
  qc.removeQueries({ queryKey: ['lead', dropId], exact: true })
  spliceLeadIntoCache(qc, kept)
}
